'use client';

import { useEffect, useState } from 'react';

import { storeClock } from '@eliadmin/core/src/utils/nt/store-clock';
import { splitOptions } from '@eliadmin/core/src/utils/nt/text';

const isWorkingDay = (date: Date) => date.getUTCDay() >= 1 && date.getUTCDay() <= 5;

function nextWorkingDay(date: Date) {
  const next = new Date(date);
  do next.setUTCDate(next.getUTCDate() + 1);
  while (!isWorkingDay(next));
  return next;
}

/** „14:00“ typed in the administration → minutes since midnight; null when it is not a time. */
function parseCutoff(text: string) {
  const match = /^(\d{1,2})[:.](\d{2})$/.exec(text.trim());
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
}

/**
 * „vo štvrtok“ and „vo štvrtok 22. 10.“ of a working day; `weekdays` = the five phrases of „Produkt -
 * dni“ (Monday first – shipping and delivery days are always working days).
 */
function formatDay(date: Date, weekdays: string[], locale: string) {
  const phrase = weekdays[date.getUTCDay() - 1] ?? '';
  const day = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'numeric',
    timeZone: 'UTC',
  }).format(date);
  return { phrase, withDate: `${phrase} ${day}` };
}

export type ShippingDates = {
  /** Ships today when ordered before the cut-off. */
  shipsToday: boolean;
  /** `{den}` of „Odosielame {den}“ – the next working day („v pondelok“). */
  shipDay: string;
  /** `{datum}` of „Doručenie už {datum}“ – the working day after shipping („vo štvrtok 22. 10.“). */
  deliveryDate: string;
};

/**
 * Shipping and delivery days for an in-stock product: shipped today on a working day before the
 * cut-off, otherwise on the next working day; delivered on the working day after shipping (public
 * holidays are not known to the store). Computed on the client after mount and refreshed every
 * minute, so the cached (ISR) page never carries a frozen date; null before mount or without a valid
 * cut-off.
 */
export function useShippingDates(
  cutoff: string,
  weekdays: string,
  locale: string,
): ShippingDates | null {
  const [now, setNow] = useState<Date | null>(null);

  useEffect(() => {
    setNow(new Date());
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const cutoffMinutes = parseCutoff(cutoff);
  const phrases = splitOptions(weekdays);
  if (!now || cutoffMinutes == null || phrases.length !== 5) return null;

  const { today, minutes } = storeClock(now);
  const shipsToday = isWorkingDay(today) && minutes < cutoffMinutes;
  const shipDate = shipsToday ? today : nextWorkingDay(today);
  return {
    shipsToday,
    shipDay: formatDay(shipDate, phrases, locale).phrase,
    deliveryDate: formatDay(nextWorkingDay(shipDate), phrases, locale).withDate,
  };
}
